import http from '../../shared/http.js';
import { verlaufParentPfad } from '../../shared/verlaufParent.js';
import { initFormModalLinks, openFormModal } from '../../shared/formModal.js';
import {
  confirmAction,
  hideLoading,
  notifyError,
  notifySuccess,
  showLoading
} from '../../shared/notify.js';

// A record's documentation column: it appends the next page as the reader
// approaches the bottom, and handles the edit and delete buttons on the notes.
//
// Drives every entity that has a timeline - the parent comes out of the scroll
// container's dataset (shared/verlaufParent.js) rather than being assumed to be
// a customer, so the customer's and the candidate's columns are one module.
//
// Unlike shared/liveList.js this one *appends* rather than replacing a region,
// and it pages by an opaque cursor rather than by a query string - different
// enough to be its own module, similar enough to speak the same X-Fragment
// protocol to the server.

// Marks the request as wanting only the timeline partial. Same protocol as the
// list fragments - see utils/renderFragment.js.
const FRAGMENT_HEADER = 'X-Fragment';

// How close to the bottom the reader gets before the next page is fetched. Far
// enough that the entries are usually there by the time they arrive.
const VORLAUF_PX = 200;

export function initKundeVerlauf() {
  const scroll = document.getElementById('verlauf-scroll');
  if (!scroll) return;

  const parentPfad = verlaufParentPfad(scroll.dataset);
  if (!parentPfad) return;
  const filterForm = document.getElementById('verlauf-filter');
  let laufend = false;

  // The current filter, as the query string the server reads. Taken from the
  // form rather than remembered, so the checkboxes are the single source of
  // truth and a reload with a URL agrees with them.
  function filterParams() {
    const params = new window.URLSearchParams();
    if (!filterForm) return params;

    new window.FormData(filterForm).forEach((value, key) => params.append(key, value));
    return params;
  }

  // Replaces the list with a freshly fetched first page. Used after a save or a
  // delete, and when the filter changes: it drops any further pages the reader
  // had scrolled through, which keeps the column consistent without patching
  // individual rows in place.
  async function neuLaden() {
    const liste = document.getElementById('verlauf-liste');
    if (!liste) return;

    const params = filterParams();
    const query = params.toString();

    try {
      const html = await seiteHolen(query ? `?${query}` : '');
      liste.innerHTML = html;
    } catch {
      // Nothing was swapped in, so the column the reader is looking at stays
      // valid. A reload is the honest fallback.
      window.location.reload();
    }
  }

  // Fetches one page of the timeline as markup.
  async function seiteHolen(query) {
    const response = await window.fetch(`/${parentPfad}/verlauf${query}`, {
      headers: { [FRAGMENT_HEADER]: '1' },
      credentials: 'same-origin'
    });

    // A dead session redirects to /login, which arrives here as a page rather
    // than our partial. Follow it rather than pasting a login form into the
    // column.
    if (response.redirected) {
      window.location.href = response.url;
      throw new Error('Sitzung abgelaufen');
    }

    if (!response.ok) throw new Error(`Verlauf-Anfrage fehlgeschlagen: ${response.status}`);

    return response.text();
  }

  // Appends the next page. Guarded against overlapping calls, because the
  // observer and the scroll listener both lead here and would otherwise fetch
  // the same cursor twice.
  async function mehrLaden() {
    const mehr = document.getElementById('verlauf-mehr');
    if (!mehr || laufend) return;

    laufend = true;
    const button = mehr.querySelector('button');
    if (button) button.disabled = true;

    const params = filterParams();
    params.set('before', mehr.dataset.next);

    try {
      const html = await seiteHolen(`?${params.toString()}`);
      const liste = document.getElementById('verlauf-liste');

      // The fetched page carries its own load-more element, or none when the
      // history is exhausted, so the loop ends itself and the cursor is always
      // the server's.
      mehr.remove();
      // insertAdjacentHTML rather than innerHTML +=: the latter reparses every
      // entry already on screen, which loses the scroll position and costs more
      // with each page.
      liste?.insertAdjacentHTML('beforeend', html);

      beobachten();
    } catch {
      if (button) button.disabled = false;
      notifyError('Weitere Einträge konnten nicht geladen werden.');
    } finally {
      laufend = false;
    }
  }

  // Watches the load-more element so the next page starts arriving before the
  // reader reaches it. Re-armed after every append, since the element that was
  // being watched has just been replaced.
  let observer = null;
  function beobachten() {
    observer?.disconnect();

    const mehr = document.getElementById('verlauf-mehr');
    if (!mehr || !window.IntersectionObserver) return;

    observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) mehrLaden();
      },
      { rootMargin: `${VORLAUF_PX}px` }
    );
    observer.observe(mehr);
  }

  // The column scrolls internally on a wide screen and with the document below
  // that, and the observer above only covers one of the two reliably. Listening
  // here as well costs a few lines and works at every width.
  scroll.addEventListener('scroll', () => {
    if (scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - VORLAUF_PX) mehrLaden();
  });

  beobachten();

  // Filtering refetches the first page rather than navigating: the rest of the
  // customer page is unaffected by it.
  filterForm?.addEventListener('change', () => neuLaden());
  filterForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    neuLaden();
  });

  // Writing a note opens the shared dialog. The link keeps its href, so a
  // middle click still opens the real page.
  initFormModalLinks({
    selector: 'a[href$="/verlauf/new"]',
    onSaved: () => neuLaden()
  });

  // One delegated listener for the whole column, so the rows that arrive with
  // each appended page are live without rebinding anything. The load-more
  // button rides along here too, which is what makes it work for a keyboard.
  scroll.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    const { action, id } = button.dataset;

    if (action === 'mehr') {
      mehrLaden();
      return;
    }

    if (action === 'edit') {
      openFormModal(`/${parentPfad}/verlauf/${id}/edit`, () => neuLaden());
      return;
    }

    if (action !== 'delete') return;

    const confirmed = await confirmAction({
      title: 'Eintrag löschen',
      message:
        'Möchten Sie diesen Eintrag endgültig löschen? Das kann nicht rückgängig gemacht werden.',
      okText: 'Löschen'
    });
    if (!confirmed) return;

    button.disabled = true;
    showLoading('Eintrag wird gelöscht …');

    try {
      await http.delete(`/verlauf/${id}`);
      await neuLaden();
      hideLoading();
      notifySuccess('Eintrag wurde gelöscht.');
    } catch (err) {
      hideLoading();
      button.disabled = false;
      notifyError(
        err.response?.data?.message || 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.'
      );
    }
  });
}
